import React, { useEffect, useState, useMemo } from 'react';
import ModalForm from '../Reusable/ModalForm';
import { SubmitButton } from '../../enums/GlobalEnums';
import { ModalForAddOrUpdateVehicle } from '../../enums/VehicleEnums';
import { useSelector, useDispatch } from 'react-redux';
import { findVehicleModelByBrandId } from '../../actions/vehicleModel';
import { findAllBrand } from '../../actions/brand';
import { findAllUsers } from '../../actions/user';
import dayjs from 'dayjs';

const ModalForUpdateVehicle = ({
  vehicle,
  updateVehicle,
  handleClose,
  show,
  vehicleReducer,
}) => {
  const dispatch = useDispatch();
  const { error } = vehicleReducer;

  const { brandList } = useSelector((state) => state.brand);
  const { userList } = useSelector((state) => state.user);
  const { vehicleModelList } = useSelector((state) => state.vehicleModel);

  const [selectedBrandId, setSelectedBrandId] = useState('');

  const isReady =
    brandList?.length &&
    userList?.length &&
    vehicleModelList;

  useEffect(() => {
    if (show) {
      dispatch(findAllBrand());
      dispatch(findAllUsers());
    }
  }, [dispatch, show]);

  // when modal is opened load models for selected brand
  useEffect(() => {
    if (show && vehicle?.brand?.id) {
      setSelectedBrandId(vehicle.brand.id);
      dispatch(findVehicleModelByBrandId(vehicle.brand.id));
    }
  }, [show, vehicle, dispatch]);

  // on brand change
  useEffect(() => {
    if (selectedBrandId) {
      dispatch(findVehicleModelByBrandId(selectedBrandId));
    }
  }, [selectedBrandId, dispatch]);

  const fields = [
    {
      name: 'vehicleName',
      label: ModalForAddOrUpdateVehicle.vehicleNamePlaceholder,
      type: 'text',
      fullWidth: true,
    },
    {
      name: 'brand.id',
      label: 'Brend',
      type: 'select',
      options: brandList?.map((b) => ({
        value: b.id,
        label: b.brandName,
      })),
      onChange: (value) => setSelectedBrandId(value),
    },
    {
      name: 'vehicleModel.id',
      label: 'Model',
      type: 'select',
      options: vehicleModelList?.map((m) => ({
        value: m.id,
        label: m.modelName,
      })),
    },
    {
      name: 'licensePlateNumber',
      label: 'Registarski broj',
      type: 'text',
    },
    {
      name: 'chassisNumber',
      label: 'Broj šasije',
      type: 'text',
    },
    {
      name: 'user.id',
      label: 'Korisnik',
      type: 'select',
      options: userList?.map((u) => ({
        value: u.id,
        label: u.username,
      })),
    },
    {
      name: 'driver.id',
      label: 'Vozač',
      type: 'select',
      options: userList?.map((u) => ({
        value: u.id,
        label: u.username,
      })),
    },
    {
      name: 'vehicleType',
      label: 'Tip vozila',
      type: 'select',
      options: [
        { value: 'Putničko vozilo', label: 'Putničko vozilo' },
        { value: 'Teretno vozilo', label: 'Teretno vozilo' },
        { value: 'Terensko vozilo', label: 'Terensko vozilo' },
      ],
    },
    {
      name: 'maximumAuthorisedMass',
      label: 'Maksimalna dozvoljena masa',
      type: 'number',
    },
    {
      name: 'fuelConsumptionPer100Kilometers',
      label: "Potrosnja na 100km",
      type: 'number',
    },
    {
      name: 'mass',
      label: 'Masa',
      type: 'number',
    },
    {
      name: 'firstRegistration',
      label: 'Datum prve registracije',
      type: 'date',
    },
    {
      name: 'expiryDateOfLicensePlateNumber',
      label: 'Datum zamene tablica',
      type: 'date',
    },
    {
      name: 'typeApprovalMark',
      label: 'Homologacija',
      type: 'text',
    },
    {
      name: 'engineDisplacement',
      label: 'Zapremina motora',
      type: 'number',
    },
    {
      name: 'enginePowerInKW',
      label: 'Snaga (kW)',
      type: 'number',
    },
    {
      name: 'fuelType',
      label: 'Gorivo',
      type: 'text',
    },
    {
      name: 'seatingCapacity',
      label: 'Broj mesta (sedenje)',
      type: 'number',
    },
    {
      name: 'standingCapacity',
      label: 'Broj mesta (stajanje)',
      type: 'number',
    },
  ];

  // initialValues
  const initialValues = useMemo(() => ({
    vehicleName: vehicle?.vehicleName || '',
    ['brand.id']: vehicle?.brand?.id || '',
    ['vehicleModel.id']: vehicle?.vehicleModel?.id || '',
    firstRegistration: vehicle?.firstRegistration
      ? dayjs(vehicle.firstRegistration, 'DD.MM.YYYY')
      : '',
    licensePlateNumber: vehicle?.licensePlateNumber || '',
    chassisNumber: vehicle?.chassisNumber || '',
    ['user.id']: vehicle?.user?.id || '',
    ['driver.id']: vehicle?.driver?.id || '',
    vehicleType: vehicle?.vehicleType || '',
    maximumAuthorisedMass: vehicle?.maximumAuthorisedMass || '',
    mass: vehicle?.mass || '',
    expiryDateOfLicensePlateNumber: vehicle?.expiryDateOfLicensePlateNumber
      ? dayjs(vehicle.expiryDateOfLicensePlateNumber, 'DD.MM.YYYY')
      : '',
    typeApprovalMark: vehicle?.typeApprovalMark || '',
    engineDisplacement: vehicle?.engineDisplacement || '',
    enginePowerInKW: vehicle?.enginePowerInKW || '',
    fuelType: vehicle?.fuelType || '',
    seatingCapacity: vehicle?.seatingCapacity || '',
    standingCapacity: vehicle?.standingCapacity || '',
    fuelConsumptionPer100Kilometers: vehicle?.fuelConsumptionPer100Kilometers || '',
  }), [vehicle]);

  const handleSubmit = (formData) => {
    const payload = {
      id: vehicle.id,
      vehicleName: formData.vehicleName,
      brand: { id: Number(formData['brand.id']) },
      vehicleModel: { id: Number(formData['vehicleModel.id']) },
      user: { id: Number(formData['user.id']) },
      driver: { id: Number(formData['driver.id']) },
      vehicleType: formData.vehicleType,
      firstRegistration: dayjs(formData.firstRegistration).format('DD.MM.YYYY'),
      licensePlateNumber: formData.licensePlateNumber,
      chassisNumber: formData.chassisNumber,
      maximumAuthorisedMass: Number(formData.maximumAuthorisedMass),
      mass: Number(formData.mass),
      expiryDateOfLicensePlateNumber: dayjs(formData.expiryDateOfLicensePlateNumber).format('DD.MM.YYYY'),
      typeApprovalMark: formData.typeApprovalMark,
      engineDisplacement: Number(formData.engineDisplacement),
      enginePowerInKW: Number(formData.enginePowerInKW),
      fuelType: formData.fuelType,
      seatingCapacity: Number(formData.seatingCapacity),
      standingCapacity: Number(formData.standingCapacity),
      fuelConsumptionPer100Kilometers: Number(formData.fuelConsumptionPer100Kilometers),

    };

    updateVehicle(payload);
  };

  if (!isReady) {
    return null
  }

  return (
    <ModalForm
      open={show}
      onClose={handleClose}
      title={ModalForAddOrUpdateVehicle.titleForUpdateVehicle}
      fields={fields}
      onSubmit={handleSubmit}
      initialValues={initialValues}
      error={error}
      submitLabel={SubmitButton.update}
      maxWidth="xl"
      grid
    />
  );
};

export default ModalForUpdateVehicle;